/**
 * Operational status rules shared by the API (which computes them from stored data) and the clients
 * (which only display them). Every rule is a pure function of recorded facts and the tenant's alert
 * settings; nothing here is assigned by hand. Dates are ISO calendar dates (YYYY-MM-DD) compared in
 * UTC, which is how every other date in the system is stored.
 */

/** Semantic tones used for every status badge, alert row and chart that represents a status. */
export type Tone = 'info' | 'success' | 'warning' | 'critical' | 'pending' | 'neutral';

export interface AlertSettings {
  /** Receivables / payables: an unpaid balance due within this many days is "due soon". */
  paymentDueSoonDays: number;
  /** Carrier invoices: an unpaid carrier invoice due within this many days is "due soon". */
  carrierDueSoonDays: number;
  /** Shipments: an undelivered shipment expected within this many days is checked for risk. */
  deliveryRiskDays: number;
  /** Shipments: no tracking update for this many days on an open shipment is "stale". */
  trackingStaleDays: number;
  /** Inventory: a batch expiring within this many days is "approaching expiry". */
  expiryWarningDays: number;
  /** Credit: exposure at or above this share of the credit limit (basis points) is a warning. */
  creditWarningBp: number;
}

export const DEFAULT_ALERT_SETTINGS: AlertSettings = {
  paymentDueSoonDays: 7,
  carrierDueSoonDays: 7,
  deliveryRiskDays: 2,
  trackingStaleDays: 3,
  expiryWarningDays: 90,
  creditWarningBp: 8000,
};

const DAY = 86_400_000;

export function isoDay(d: Date | string): string {
  return typeof d === 'string' ? d.slice(0, 10) : d.toISOString().slice(0, 10);
}

export function addDays(day: string, n: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + n * DAY).toISOString().slice(0, 10);
}

/** Whole days from `from` to `to` (positive when `to` is later). */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to.slice(0, 10)}T00:00:00Z`) - Date.parse(`${from.slice(0, 10)}T00:00:00Z`)) / DAY);
}

// ---------------------------------------------------------------- Customer receivables

export type ReceivableStatus = 'not_due' | 'pending' | 'due_soon' | 'overdue' | 'partially_paid' | 'paid';

export interface ReceivableFacts {
  totalMinor: number;
  /** Confirmed (status "received") payments allocated to the order, net of reversals. */
  receivedMinor: number;
  /** Due date from the existing rule: first non-cancelled dispatch + payment terms' balance days. Null before dispatch. */
  dueDate: string | null;
}

export interface ReceivableState {
  status: ReceivableStatus;
  /** Partial payment flag; a partially paid balance can still be due soon or overdue. */
  partiallyPaid: boolean;
  outstandingMinor: number;
  daysOverdue: number;
  tone: Tone;
}

/**
 * Receivable status of one order. Only confirmed payments reduce the balance, so an uploaded receipt
 * or a payment still awaiting confirmation never makes anything "paid". An order with no dispatch yet
 * has no due date under the existing payment-terms rule and is "not yet due".
 */
export function receivableState(f: ReceivableFacts, today: string, s: Pick<AlertSettings, 'paymentDueSoonDays'> = DEFAULT_ALERT_SETTINGS): ReceivableState {
  const outstanding = Math.max(0, f.totalMinor - f.receivedMinor);
  const partiallyPaid = f.receivedMinor > 0 && outstanding > 0;
  if (outstanding === 0) return { status: 'paid', partiallyPaid: false, outstandingMinor: 0, daysOverdue: 0, tone: 'success' };
  if (!f.dueDate) return { status: partiallyPaid ? 'partially_paid' : 'not_due', partiallyPaid, outstandingMinor: outstanding, daysOverdue: 0, tone: partiallyPaid ? 'pending' : 'neutral' };
  const late = daysBetween(f.dueDate, today);
  if (late > 0) return { status: 'overdue', partiallyPaid, outstandingMinor: outstanding, daysOverdue: late, tone: 'critical' };
  if (-late <= s.paymentDueSoonDays) return { status: 'due_soon', partiallyPaid, outstandingMinor: outstanding, daysOverdue: 0, tone: 'warning' };
  return { status: partiallyPaid ? 'partially_paid' : 'pending', partiallyPaid, outstandingMinor: outstanding, daysOverdue: 0, tone: partiallyPaid ? 'pending' : 'info' };
}

export type AgeingBucket = 'not_due' | 'd1_30' | 'd31_60' | 'd61_90' | 'd90_plus';
export const AGEING_BUCKETS: readonly { key: AgeingBucket; label: string }[] = [
  { key: 'not_due', label: 'Not yet due' },
  { key: 'd1_30', label: '1–30 days overdue' },
  { key: 'd31_60', label: '31–60 days overdue' },
  { key: 'd61_90', label: '61–90 days overdue' },
  { key: 'd90_plus', label: 'More than 90 days overdue' },
];

export function ageingBucket(daysOverdue: number): AgeingBucket {
  if (daysOverdue <= 0) return 'not_due';
  if (daysOverdue <= 30) return 'd1_30';
  if (daysOverdue <= 60) return 'd31_60';
  if (daysOverdue <= 90) return 'd61_90';
  return 'd90_plus';
}

// ---------------------------------------------------------------- Shipments

/** Logistics milestones recorded against a shipment. `status` on the shipment remains the fulfilment state. */
export const TRACKING_STATUSES = ['dispatched', 'in_transit', 'out_for_delivery', 'delivery_attempted', 'delayed', 'exception', 'delivered', 'returned', 'cancelled'] as const;
export type TrackingStatus = (typeof TRACKING_STATUSES)[number];

export const TRACKING_LABELS: Record<TrackingStatus, string> = {
  dispatched: 'Dispatched',
  in_transit: 'In transit',
  out_for_delivery: 'Out for delivery',
  delivery_attempted: 'Delivery attempted',
  delayed: 'Delayed',
  exception: 'Exception',
  delivered: 'Delivered',
  returned: 'Returned',
  cancelled: 'Cancelled',
};

export type DeliveryRisk = 'delivered_on_time' | 'delivered_late' | 'delivered' | 'cancelled' | 'returned' | 'missed' | 'at_risk' | 'exception' | 'on_track' | 'no_commitment';

export interface ShipmentFacts {
  status: string; // fulfilment status: dispatched | in_transit | delivered | cancelled
  trackingStatus: TrackingStatus | null;
  expectedDeliveryDate: string | null;
  deliveredAt: string | null;
  lastTrackingAt: string | null;
  dispatchedAt: string;
}

export interface ShipmentState {
  risk: DeliveryRisk;
  tone: Tone;
  label: string;
  /** No tracking update within the stale window on an open shipment. */
  stale: boolean;
  daysLate: number;
}

/**
 * Delivery risk from recorded facts. A tracking number alone never implies progress. Delivered
 * shipments are judged against the expected date; open shipments are missed once the date has passed,
 * at risk when it is close and the last milestone signals trouble or tracking has gone quiet.
 */
export function shipmentState(f: ShipmentFacts, today: string, s: Pick<AlertSettings, 'deliveryRiskDays' | 'trackingStaleDays'> = DEFAULT_ALERT_SETTINGS): ShipmentState {
  const lastSignal = isoDay(f.lastTrackingAt ?? f.dispatchedAt);
  const open = f.status === 'dispatched' || f.status === 'in_transit';
  const stale = open && f.trackingStatus !== 'returned' && daysBetween(lastSignal, today) >= s.trackingStaleDays;
  if (f.status === 'cancelled') return { risk: 'cancelled', tone: 'neutral', label: 'Cancelled', stale: false, daysLate: 0 };
  if (f.status === 'delivered') {
    if (!f.expectedDeliveryDate || !f.deliveredAt) return { risk: 'delivered', tone: 'success', label: 'Delivered', stale: false, daysLate: 0 };
    const late = daysBetween(f.expectedDeliveryDate, isoDay(f.deliveredAt));
    return late > 0
      ? { risk: 'delivered_late', tone: 'warning', label: `Delivered ${late} d late`, stale: false, daysLate: late }
      : { risk: 'delivered_on_time', tone: 'success', label: 'Delivered on time', stale: false, daysLate: 0 };
  }
  if (f.trackingStatus === 'returned') return { risk: 'returned', tone: 'critical', label: 'Returned', stale: false, daysLate: 0 };
  if (f.trackingStatus === 'exception') return { risk: 'exception', tone: 'critical', label: 'Delivery exception', stale, daysLate: 0 };
  if (f.expectedDeliveryDate) {
    const late = daysBetween(f.expectedDeliveryDate, today);
    if (late > 0) return { risk: 'missed', tone: 'critical', label: `Missed by ${late} d`, stale, daysLate: late };
    const troubled = f.trackingStatus === 'delayed' || f.trackingStatus === 'delivery_attempted';
    if (troubled || (-late <= s.deliveryRiskDays && stale)) return { risk: 'at_risk', tone: 'warning', label: 'At risk', stale, daysLate: 0 };
    return { risk: 'on_track', tone: 'info', label: f.trackingStatus === 'out_for_delivery' ? 'Out for delivery' : 'On track', stale, daysLate: 0 };
  }
  if (f.trackingStatus === 'delayed' || f.trackingStatus === 'delivery_attempted') return { risk: 'at_risk', tone: 'warning', label: TRACKING_LABELS[f.trackingStatus], stale, daysLate: 0 };
  return { risk: 'no_commitment', tone: 'info', label: 'No delivery date', stale, daysLate: 0 };
}

// ---------------------------------------------------------------- Carrier invoices (payables)

export type CarrierInvoiceStatus = 'received' | 'approved' | 'disputed' | 'cancelled';
export type CarrierPayableStatus = 'awaiting_approval' | 'disputed' | 'cancelled' | 'pending' | 'due_soon' | 'overdue' | 'partially_paid' | 'paid';

export interface CarrierInvoiceFacts {
  status: CarrierInvoiceStatus;
  totalMinor: number;
  paidMinor: number;
  dueDate: string | null;
}

export interface CarrierPayableState {
  status: CarrierPayableStatus;
  partiallyPaid: boolean;
  outstandingMinor: number;
  daysOverdue: number;
  tone: Tone;
}

/**
 * Payable state of a carrier invoice. Workflow status (received → approved, or disputed) and payment
 * status are separate facts: an approved invoice is not paid, and a payment never approves an invoice.
 */
export function carrierPayableState(f: CarrierInvoiceFacts, today: string, s: Pick<AlertSettings, 'carrierDueSoonDays'> = DEFAULT_ALERT_SETTINGS): CarrierPayableState {
  const outstanding = Math.max(0, f.totalMinor - f.paidMinor);
  const partiallyPaid = f.paidMinor > 0 && outstanding > 0;
  if (f.status === 'cancelled') return { status: 'cancelled', partiallyPaid: false, outstandingMinor: 0, daysOverdue: 0, tone: 'neutral' };
  if (outstanding === 0) return { status: 'paid', partiallyPaid: false, outstandingMinor: 0, daysOverdue: 0, tone: 'success' };
  const late = f.dueDate ? daysBetween(f.dueDate, today) : 0;
  if (f.dueDate && late > 0) return { status: 'overdue', partiallyPaid, outstandingMinor: outstanding, daysOverdue: late, tone: 'critical' };
  if (f.status === 'disputed') return { status: 'disputed', partiallyPaid, outstandingMinor: outstanding, daysOverdue: 0, tone: 'warning' };
  if (f.dueDate && -late <= s.carrierDueSoonDays) return { status: 'due_soon', partiallyPaid, outstandingMinor: outstanding, daysOverdue: 0, tone: 'warning' };
  if (f.status === 'received') return { status: 'awaiting_approval', partiallyPaid, outstandingMinor: outstanding, daysOverdue: 0, tone: 'pending' };
  return { status: partiallyPaid ? 'partially_paid' : 'pending', partiallyPaid, outstandingMinor: outstanding, daysOverdue: 0, tone: partiallyPaid ? 'pending' : 'info' };
}

// ---------------------------------------------------------------- Inventory expiry

export type ExpiryStatus = 'expired' | 'approaching' | 'ok' | 'no_expiry';

export function expiryState(expiryDate: string | null, today: string, s: Pick<AlertSettings, 'expiryWarningDays'> = DEFAULT_ALERT_SETTINGS): { status: ExpiryStatus; tone: Tone; daysLeft: number | null } {
  if (!expiryDate) return { status: 'no_expiry', tone: 'neutral', daysLeft: null };
  const left = daysBetween(today, expiryDate);
  if (left < 0) return { status: 'expired', tone: 'critical', daysLeft: left };
  if (left <= s.expiryWarningDays) return { status: 'approaching', tone: 'warning', daysLeft: left };
  return { status: 'ok', tone: 'success', daysLeft: left };
}

// ---------------------------------------------------------------- Freight

export type FreightArrangement = 'customer_pays_carrier' | 'company_pays_recovers' | 'company_absorbs';

export const FREIGHT_ARRANGEMENT_LABELS: Record<FreightArrangement, string> = {
  customer_pays_carrier: 'Customer pays carrier directly',
  company_pays_recovers: 'We pay carrier, recharge customer',
  company_absorbs: 'We pay carrier, freight absorbed',
};

export interface FreightFacts {
  arrangement: FreightArrangement;
  /** Agreed or quoted carrier charge (incl. tax), if known. */
  expectedCarrierMinor: number | null;
  /** Carrier charge as invoiced to us (sum of carrier invoice lines for this charge, incl. tax). */
  invoicedCarrierMinor: number;
  /** Customer freight charge we raised (incl. tax). Zero when nothing was charged. */
  customerChargeMinor: number;
  customerBilling: 'none' | 'on_order_invoice' | 'separate_invoice';
  /** Customer freight collections recorded against a separately billed charge. */
  collectedMinor: number;
}

export interface FreightPosition {
  /** Our cost basis: invoiced amount when invoiced, else the agreed amount; null when unknown. */
  companyCostMinor: number | null;
  /** Freight the company bears: cost minus what it charges the customer (never negative). */
  absorbedMinor: number | null;
  /** Customer charge not yet collected; null when collection is through order payments and cannot be attributed. */
  uncollectedMinor: number | null;
  /** Invoiced minus agreed, when both exist. */
  discrepancyMinor: number | null;
  /** Customer charge minus company cost, when both are known. */
  marginMinor: number | null;
}

/**
 * Freight economics for one charge. A customer-direct payment is never company cost or company cash;
 * an order-invoice freight charge is collected through order payments, so it is reported as billed but
 * not as collected.
 */
export function freightPosition(f: FreightFacts): FreightPosition {
  if (f.arrangement === 'customer_pays_carrier') {
    return { companyCostMinor: 0, absorbedMinor: 0, uncollectedMinor: null, discrepancyMinor: null, marginMinor: null };
  }
  const cost = f.invoicedCarrierMinor > 0 ? f.invoicedCarrierMinor : f.expectedCarrierMinor;
  const charge = f.arrangement === 'company_absorbs' ? 0 : f.customerChargeMinor;
  return {
    companyCostMinor: cost,
    absorbedMinor: cost === null ? null : Math.max(0, cost - charge),
    uncollectedMinor: f.customerBilling === 'separate_invoice' ? Math.max(0, charge - f.collectedMinor) : null,
    discrepancyMinor: f.invoicedCarrierMinor > 0 && f.expectedCarrierMinor !== null ? f.invoicedCarrierMinor - f.expectedCarrierMinor : null,
    marginMinor: cost === null || f.arrangement === 'company_absorbs' ? null : charge - cost,
  };
}
